export interface ParsedAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
}

type AddressComponentLookup = [type: string, name: "long_name" | "short_name"];

const ADDRESS_LOOKUP = {
  streetNumber: ["street_number", "long_name"],
  street: ["route", "long_name"],
  addressLine2: ["subpremise", "long_name"],
  city: ["locality", "long_name"],
  state: ["administrative_area_level_1", "short_name"],
  countryCode: ["country", "short_name"],
  zip: ["postal_code", "short_name"],
} satisfies Record<string, AddressComponentLookup>;

function findAddressPart(
  components: google.maps.GeocoderAddressComponent[],
  lookup: keyof typeof ADDRESS_LOOKUP,
): string {
  const [type, nameKey] = ADDRESS_LOOKUP[lookup];
  const part = components.find((component) => component.types.includes(type));
  return part ? part[nameKey] : "";
}

export function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[],
): ParsedAddress {
  return {
    addressLine1: [
      findAddressPart(components, "streetNumber"),
      findAddressPart(components, "street"),
    ]
      .filter(Boolean)
      .join(" "),
    addressLine2: findAddressPart(components, "addressLine2"),
    city: findAddressPart(components, "city"),
    state: findAddressPart(components, "state"),
    zip: findAddressPart(components, "zip"),
    countryCode: findAddressPart(components, "countryCode"),
  };
}

/** Single-line form used when querying the geocoder; `addressLine2` is deliberately excluded. */
export function toAddressQuery(address: ParsedAddress): string {
  return [
    address.addressLine1,
    address.city,
    address.state,
    address.zip,
    address.countryCode,
  ]
    .filter(Boolean)
    .join(", ");
}

/** "Denver, CO 80130" — the second display line in the verification modal. */
export function formatRegionLine(address: ParsedAddress): string {
  const stateZip = [address.state, address.zip].filter(Boolean).join(" ");
  return [address.city, stateZip].filter(Boolean).join(", ");
}
